import React, { FC, ReactNode, useEffect, useState } from 'react'

import { CardWrapper } from '../CardWrapper/CardWrapper'
import { CardItem } from '../CardItem/CardItem'

import {
  hasInvalidRequiredField,
  isInvalidRequired,
  isMissingRequired,
  reqFieldLabel,
} from '../../../../../../helpers/requiredFields'
import { cn } from '../../../../../../lib/utils'

import {
  IProfileReqField,
  IReqFieldType,
} from '../../../../../../pages/competitor/clinicAndOther/Tabs/components/ProfileFields'
import { IOption } from '../../../../../inputs/InputNewMemberRow'

interface CustomTitleProps {
  activeTab?: 'overview'
  errorsCount: number
  requiredField: IProfileReqField
}

const CustomTitle: FC<CustomTitleProps> = ({ requiredField, activeTab, errorsCount }) => {
  return requiredField ? (
    <>
      <p className="text-base font-medium">Required Fields</p>
      <p className="text-xs text-black/50 mt-0.5">
        {activeTab === 'overview' && <>{errorsCount <= 0 && 'No issues'}</>}
      </p>
    </>
  ) : (
    <>
      <p className="text-base font-medium">Required Fields</p>
      <p className="text-xs text-black/50 mt-0.5">No required fields found</p>
    </>
  )
}

interface RequiredFieldsCardItemProps {
  activeTab?: 'overview'
  className?: string
  isCollapsible?: boolean
  onActionClick?: () => void
  actionIcon?: ReactNode
  requiredFields: IProfileReqField[]
  isEditable?: boolean
  isEdit?: boolean
  setIsEdit?: (state: boolean) => void
  onSaveEdit?: () => Promise<void>
  onCancelEdit?: () => void
  onChange?: (state: {
    fieldName: string
    fieldType: IReqFieldType
    fieldKey?: string
    option?: IOption
  }) => void
}

export const RequiredFieldsCardItem: FC<RequiredFieldsCardItemProps> = ({
  activeTab,
  className,
  isCollapsible,
  onActionClick,
  actionIcon,
  requiredFields,
  isEditable,
  isEdit,
  setIsEdit,
  onSaveEdit,
  onCancelEdit,
  onChange,
}) => {
  const [errorsCount, setErrorsCount] = useState(0)

  const showOnlyErrors = (field: IProfileReqField) => {
    return activeTab === 'overview' ? hasInvalidRequiredField(field) : true
  }

  useEffect(() => {
    let countErrors = 0
    requiredFields?.forEach((field) => {
      if (hasInvalidRequiredField(field)) {
        countErrors += 1
      }
    })
    setErrorsCount(countErrors)
  }, [requiredFields])

  return (
    <CardWrapper
      isCollapsible={isCollapsible}
      title={
        <CustomTitle
          requiredField={requiredFields?.[0]}
          errorsCount={errorsCount}
          activeTab={activeTab}
        />
      }
      className={cn('bg-[#00000005] border-none', className)}
      titleClassName={requiredFields?.[0] ? '' : '-mb-2'}
      actionIcon={actionIcon}
      onActionClick={onActionClick}
      onSaveEdit={onSaveEdit}
      onCancelEdit={onCancelEdit}
      isEdit={isEdit}
      isEditable={isEditable}
      setIsEdit={setIsEdit}
    >
      {requiredFields.map(
        (field, index) =>
          showOnlyErrors(field) && (
            <CardItem
              key={index}
              field={field}
              label={field.title}
              url={field.fieldUrl}
              fieldType={field.type}
              fieldKey={field.fieldKey}
              defaultValue={reqFieldLabel(field)}
              isInvalid={isInvalidRequired(field)}
              isMissing={isMissingRequired(field)}
              isExpired={field.isExpired || false}
              showLabelError={true}
              isEdit={isEdit}
              onChange={({ fieldName, fieldKey, fieldType, option }) =>
                onChange && onChange({ fieldName, fieldKey, fieldType, option })
              }
            />
          )
      )}
    </CardWrapper>
  )
}
